import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * Fires when a visitor supplies email/phone on the Book form but leaves without submitting.
 * Called with the current form state and step index; parent decides when to invoke via
 * beforeunload / route change.
 *
 * We debounce with a submitted-flag so the abandonment event does NOT fire after a
 * successful submission.
 */
export function useBookAbandonment(form, step) {
  const submitted = useRef(false);
  const posted = useRef(false);
  const formRef = useRef(form);
  const stepRef = useRef(step);

  // keep refs current so send() reads the latest values (avoids stale closure)
  useEffect(() => { formRef.current = form; stepRef.current = step; });

  // expose a setter for the parent to mark success
  useEffect(() => {
    const markSubmitted = () => { submitted.current = true; };
    window.addEventListener("book:submitted", markSubmitted);
    return () => window.removeEventListener("book:submitted", markSubmitted);
  }, []);

  useEffect(() => {
    const send = () => {
      if (submitted.current || posted.current) return;
      const f = formRef.current;
      const s = stepRef.current;
      // require email + phone to be useful for follow-up
      if (!f.email || !f.phone || !f.name) return;
      posted.current = true;
      const payload = { ...f, source: "book-abandoned", step_reached: s, consent: false };
      // fire-and-forget; use sendBeacon if available for reliability on unload
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/book-abandoned`;
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
        else api.post("/book-abandoned", payload).catch(() => {});
        trackEvent("booking_abandoned", { step_reached: step });
      } catch {}
    };

    const onUnload = () => send();
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      // also fire on unmount (route change) if user had entered contact details
      send();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const markBookSubmitted = () => window.dispatchEvent(new Event("book:submitted"));
